import { Link } from "react-router-dom";
import { useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LuBed,
  LuCalendar,
  LuCreditCard,
  LuHash,
  LuMail,
  LuMapPin,
  LuPrinter,
  LuSparkles,
  LuUsers
} from "react-icons/lu";
import { MdCheckCircle, MdHome } from "react-icons/md";
import ProgressBar from "./ProgressBar";
import { ROOMS_CATALOG } from "./roomCatalog";
const parseInputDate = (value) => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};
const isSummerSeason = (date) => {
  const month = date.getMonth();
  return month >= 3 && month <= 8;
};
const Confirmation = () => {
  const router = useNavigate();
  const [searchParams] = useSearchParams();
  const bookingDetails = useMemo(() => {
    try {
      const raw = localStorage.getItem("bookingData");
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }, []);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const roomTypeParam = searchParams.get("roomType") ?? bookingDetails?.roomType;
  const checkIn = searchParams.get("checkIn") ?? bookingDetails?.checkIn;
  const checkOut = searchParams.get("checkOut") ?? bookingDetails?.checkOut;
  const guests = Number(searchParams.get("guests") ?? bookingDetails?.guests ?? "1");
  const isValidRoomType = roomTypeParam === "double" || roomTypeParam === "junior-suite" || roomTypeParam === "master-suite";
  const summary = useMemo(() => {
    if (!isValidRoomType || !checkIn || !checkOut) return null;
    const checkInDate = parseInputDate(checkIn);
    const checkOutDate = parseInputDate(checkOut);
    if (!checkInDate || !checkOutDate) return null;
    const nights = Math.ceil((checkOutDate.getTime() - checkInDate.getTime()) / (1e3 * 60 * 60 * 24));
    if (nights < 3) return null;
    const roomType = roomTypeParam;
    const season = isSummerSeason(checkInDate) ? "Summer" : "Winter";
    const baseRoom = ROOMS_CATALOG.find((item) => item.type === roomType);
    if (!baseRoom) return null;
    const rates = season === "Summer" ? baseRoom.seasonRates.summer : baseRoom.seasonRates.winter;
    const pricePerNight = guests === 1 ? rates.single : rates.double;
    return {
      roomName: baseRoom.name,
      season,
      totalPrice: pricePerNight * nights,
      nights,
      checkIn,
      checkOut,
      roomType,
      seasonRates: baseRoom.seasonRates
    };
  }, [roomTypeParam, checkIn, checkOut, guests, isValidRoomType]);
  const transactionId = useMemo(() => Math.floor(Math.random() * 1e8), []);
  const handlePrint = () => window.print();
  if (!summary || !bookingDetails) {
    return <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-emerald-50 via-white to-green-50 px-4 pt-[88px] md:pt-[108px]">
                <div className="rounded-2xl border border-emerald-200 bg-white p-8 text-center shadow-lg">
                    <p className="text-xl text-gray-700">Reservierungsinformationen wurden nicht gefunden.</p>
                    <button
      onClick={() => router("/")}
      className="mt-4 rounded-lg bg-[#1f4d3c] px-6 py-3 text-white transition-colors hover:bg-[#1a3d2e]"
    >
                        Zur Startseite
                    </button>
                </div>
            </div>;
  }
  const paymentMethod = bookingDetails.paymentMethod || "Vorauszahlung per \xDCberweisung";
  return <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-green-50 pt-[88px] md:pt-[108px]">
            <ProgressBar currentStep="confirmation" />

            <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8 md:py-12 print:py-2">
                <motion.div
    initial={{ opacity: 0, y: -20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    className="mb-8 rounded-2xl border-l-4 border-[#1f4d3c] bg-gradient-to-r from-emerald-50 to-green-50 p-6 shadow-lg md:p-8"
  >
                    <div className="flex flex-col items-center justify-center gap-4 text-center sm:flex-row sm:text-left">
                        <div className="flex-shrink-0 rounded-full bg-[#1f4d3c] p-3 md:p-4">
                            <MdCheckCircle className="h-8 w-8 text-white md:h-12 md:w-12" />
                        </div>
                        <div>
                            <h1 className="mb-2 text-2xl font-light text-gray-900 md:text-3xl">Vielen Dank für Ihre Buchung</h1>
                            <p className="text-base text-gray-700 md:text-lg">
                                Wir haben Ihre Reservierung bestätigt.
                                <span className="break-all font-semibold"> {bookingDetails.email || "your-email@example.com"}</span>
                            </p>
                        </div>
                    </div>
                </motion.div>

                <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay: 0.2 }}
    className="rounded-2xl border-l-4 border-[#1f4d3c] bg-white p-6 shadow-lg md:p-8"
  >
                    <div className="mb-6 border-b border-gray-200 pb-4">
                        <h2 className="flex items-center justify-center gap-2 text-center text-2xl font-light text-gray-900 md:text-3xl">
                            <LuSparkles className="h-6 w-6 text-[#1f4d3c] md:h-8 md:w-8" />
                            Buchungsbestätigung
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
                        {[
    { icon: LuHash, label: "Reservierungs-ID", value: String(transactionId), featured: true },
    { icon: LuMail, label: "E-Mail-Adresse", value: bookingDetails.email || "-" },
    { icon: LuMapPin, label: "Stadt", value: bookingDetails.city || "-" },
    { icon: LuBed, label: "Zimmertyp", value: summary.roomName },
    { icon: LuUsers, label: "G\xE4ste", value: `${guests} Personen` },
    { icon: LuCalendar, label: "Check-in", value: new Date(summary.checkIn).toLocaleDateString() },
    { icon: LuCalendar, label: "Check-out", value: new Date(summary.checkOut).toLocaleDateString() },
    { icon: LuCreditCard, label: "Zahlungsart", value: paymentMethod }
  ].map((item) => <motion.div
    key={item.label}
    whileHover={{ scale: 1.02 }}
    className={`${item.featured ? "border-emerald-200 bg-gradient-to-r from-emerald-50 to-green-50" : "border-gray-200 bg-gray-50"} rounded-xl border p-4`}
  >
                                <div className="flex items-start gap-3">
                                    <item.icon className="mt-1 h-5 w-5 flex-shrink-0 text-[#1f4d3c]" />
                                    <div className="min-w-0 flex-1">
                                        <p className="mb-1 text-xs font-medium text-gray-600 md:text-sm">{item.label}</p>
                                        <p className="break-all text-sm font-medium text-gray-900 md:text-base">{item.value}</p>
                                    </div>
                                </div>
                            </motion.div>)}

                        <motion.div
    whileHover={{ scale: 1.01 }}
    className="rounded-xl border border-gray-200 bg-gray-50 p-4 md:col-span-2"
  >
                            <p className="mb-3 text-sm font-medium text-gray-700">Preise je nach Saison</p>
                            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                <div className={`rounded-lg border p-2.5 ${summary.season === "Summer" ? "border-amber-300 bg-amber-100/50" : "border-gray-200 bg-white"}`}>
                                    <div className="mb-1.5 flex items-center justify-between">
                                        <span className="text-xs font-semibold text-amber-700">Sommer</span>
                                        {summary.season === "Summer" && <span className="rounded-full bg-[#1f4d3c] px-2 py-0.5 text-xs text-white">Aktuell</span>}
                                    </div>
                                    <div className="space-y-1 text-xs">
                                        <div className="flex justify-between"><span className="text-gray-600">1 Person</span><span className="font-semibold text-gray-700">EUR {summary.seasonRates.summer.single}/Nacht</span></div>
                                        <div className="flex justify-between"><span className="text-gray-600">2 Personen</span><span className="font-semibold text-gray-700">EUR {summary.seasonRates.summer.double}/Nacht</span></div>
                                    </div>
                                    <p className="mt-1 text-xs text-gray-500">Mai bis Oktober</p>
                                </div>

                                <div className={`rounded-lg border p-2.5 ${summary.season === "Winter" ? "border-emerald-300 bg-emerald-100/50" : "border-gray-200 bg-white"}`}>
                                    <div className="mb-1.5 flex items-center justify-between">
                                        <span className="text-xs font-semibold text-emerald-700">Winter</span>
                                        {summary.season === "Winter" && <span className="rounded-full bg-[#1f4d3c] px-2 py-0.5 text-xs text-white">Aktuell</span>}
                                    </div>
                                    <div className="space-y-1 text-xs">
                                        <div className="flex justify-between"><span className="text-gray-600">1 Person</span><span className="font-semibold text-gray-700">EUR {summary.seasonRates.winter.single}/Nacht</span></div>
                                        <div className="flex justify-between"><span className="text-gray-600">2 Personen</span><span className="font-semibold text-gray-700">EUR {summary.seasonRates.winter.double}/Nacht</span></div>
                                    </div>
                                    <p className="mt-1 text-xs text-gray-500">November bis April</p>
                                </div>
                            </div>

                            <motion.div
    whileHover={{ scale: 1.02 }}
    className="mt-4 rounded-xl border-l-4 border-[#1f4d3c] bg-gradient-to-r from-emerald-50 to-green-50 p-5"
  >
                                <div className="flex items-center justify-between">
                                    <div className="flex flex-col gap-1">
                                        <p className="text-base font-bold text-gray-900 md:text-lg">Gesamtbetrag</p>
                                        <p className="text-xs text-gray-600">
                                            {guests === 1 ? "1 Person" : "2 Personen"} · {summary.season === "Summer" ? "Sommer" : "Winter"}
                                        </p>
                                    </div>
                                    <p className="text-2xl font-bold text-[#1f4d3c] md:text-3xl">EUR {summary.totalPrice.toFixed(2)}</p>
                                </div>
                            </motion.div>
                        </motion.div>
                    </div>
                </motion.div>

                <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay: 0.2 }}
    className="mt-8 rounded-2xl border border-emerald-200 bg-white p-6 shadow-lg md:p-8"
  >
                    <div className="mb-4 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 text-[#1f4d3c]">
                            <LuPrinter className="h-5 w-5" />
                            <h3 className="text-xl font-light text-gray-900">Buchungsdetails</h3>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                            <MdHome className="h-4 w-4" />
                            <Link to="/" className="font-medium text-[#1f4d3c] hover:underline">Zur Startseite</Link>
                        </div>
                    </div>

                    <div className="flex flex-col gap-3 sm:flex-row">
                        <button
    onClick={handlePrint}
    className="flex items-center justify-center gap-2 rounded-lg bg-[#1f4d3c] px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#1a3d2e]"
  >
                            <LuPrinter className="h-4 w-4" />
                            Buchungsdetails drucken
                        </button>
                        <button
    onClick={() => router("/")}
    className="flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-semibold text-gray-800 transition-colors hover:bg-gray-100"
  >
                            <MdHome className="h-4 w-4" />
                            Zur Startseite
                        </button>
                    </div>
                </motion.div>
            </div>
        </div>;
};
export default Confirmation;
